import React, { useEffect, useState } from 'react';
import { PhoneIcon, CheckCircle2Icon, HomeIcon } from 'lucide-react';
import { CashOfferForm } from './CashOfferForm';
export function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <section className="relative min-h-[85vh] flex flex-col" data-id="element-147">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 overflow-hidden" data-id="element-148">
        <img src="https://cdn.magicpatterns.com/uploads/bMfFrBYTHetxRuERXmVhBk/81C58552-662F-41A3-BB24-73F62C6E7F15.png" alt="Florida single family home with palm trees" className="w-full h-full object-cover" data-id="element-149" />
        <div className="absolute inset-0 bg-[#1B2A4A]/75" data-id="element-150" />
        {/* Softer Vignette Overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.2)_100%)] pointer-events-none" data-id="element-151" />
      </div>

      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-152">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center" data-id="element-153">
          <div className={`flex items-center gap-2.5 text-2xl font-bold tracking-tight transition-colors ${scrolled ? 'text-[#1B2A4A]' : 'text-white'}`} data-id="element-154">
            <div className="bg-[#E8913A] text-white p-1.5 rounded-lg shadow-sm flex items-center justify-center" data-id="element-155">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" data-id="element-156">
                <path d="M11.47 3.84a.75.75 0 011.06 0l8.99 9a.75.75 0 11-1.06 1.06l-1.21-1.21V20a1 1 0 01-1 1h-4.5a1 1 0 01-1-1v-4a1 1 0 00-1-1h-2a1 1 0 00-1 1v4a1 1 0 01-1 1H4.75a1 1 0 01-1-1v-7.31l-1.21 1.21a.75.75 0 11-1.06-1.06l8.99-9z" data-id="element-157" />
              </svg>
            </div>
            <span data-id="element-158">
              Bevo Coastal{' '}
              <span className={scrolled ? 'text-[#E8913A]' : 'text-white/80'} data-id="element-159">
                Investments
              </span>
            </span>
          </div>
          <div className="flex items-center gap-6" data-id="element-160">
            <a href="tel:8883417570" className={`hidden md:flex items-center font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-[#E8913A]' : 'text-white/90 hover:text-white'}`} data-id="element-161">
              <PhoneIcon className="w-4 h-4 mr-2 opacity-70" data-id="element-162" />
              (888) 341-7570
            </a>
            <button onClick={() => {
            const form = document.getElementById('hero-form');
            form?.scrollIntoView({
              behavior: 'smooth'
            });
          }} className={`hidden sm:inline-flex font-semibold py-2.5 px-5 rounded-md transition-all text-sm ${scrolled ? 'bg-[#E8913A] hover:bg-[#D17E2E] text-white shadow-sm' : 'bg-white text-[#1B2A4A] hover:bg-white/90'}`} data-id="element-163">
              Request Offer
            </button>
          </div>
        </div>
      </header>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex items-center pt-24 pb-16" data-id="element-164">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-165">
          {/* Left Column */}
          <div className="lg:col-span-7 max-w-2xl" data-id="element-166">
            <div className="inline-block mb-6" data-id="element-167">
              <span className="bg-[#E8913A]/90 text-white text-sm font-semibold tracking-wide uppercase rounded-full px-5 py-2 shadow-lg backdrop-blur-sm" data-id="element-168">
                Trusted by 500+ Florida homeowners
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.05] mb-6 tracking-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.3)]" data-id="element-169">
              Sell Your Home
              <br data-id="element-170" />
              on{' '}
              <span className="border-b-4 border-[#E8913A] pb-1" data-id="element-171">
                Your Terms.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-white/85 mb-10 leading-relaxed max-w-xl drop-shadow-[0_1px_4px_rgba(0,0,0,0.2)]" data-id="element-172">
              Get a guaranteed cash offer within 24 hours. No repairs. No fees.
              Close in as little as 7 days.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-10 mb-10" data-id="element-173">
              {['No Commissions', 'No Closing Costs', 'Close On Your Timeline', 'Sell As-Is'].map(badge => <div key={badge} className="flex items-center text-lg font-semibold text-white drop-shadow-sm border-l-2 border-[#E8913A] pl-4" data-id="element-174">
                  <CheckCircle2Icon className="w-6 h-6 text-[#E8913A] mr-3 flex-shrink-0" data-id="element-175" />
                  {badge}
                </div>)}
            </div>

            <a href="tel:8883417570" className="inline-flex items-center text-white font-medium hover:text-white/80 transition-colors md:hidden" data-id="element-176">
              <PhoneIcon className="w-5 h-5 mr-2" data-id="element-177" />
              Call us: (888) 341-7570
            </a>
          </div>

          {/* Right Column: Form */}
          <div className="lg:col-span-5 w-full max-w-md mx-auto lg:ml-auto lg:mr-0" id="hero-form" data-id="element-178">
            <div className="bg-white/95 backdrop-blur-xl p-6 md:p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/20" data-id="element-179">
              <div className="mb-6" data-id="element-180">
                <h2 className="text-2xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-2" data-id="element-181">
                  <HomeIcon className="w-5 h-5 text-[#E8913A]" data-id="element-182" />
                  Get Your Free Cash Offer
                </h2>
                <p className="text-slate-500 text-sm" data-id="element-183">
                  Takes less than 60 seconds &middot; 100% Free
                </p>
              </div>
              <CashOfferForm buttonText="Request Offer" data-id="element-184" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}